import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import gallery1 from '../../images/gallery-1.jpg';
import gallery2 from '../../images/gallery-2.jpg';
import gallery3 from '../../images/gallery-3.jpg';
import food1 from '../../images/food-1.jpg';
import food2 from '../../images/food-2.jpg';
import chefCooking from '../../images/chef-cooking.jpg';
import { useSlider, useScrollReveal } from '../../hooks';
import type { GalleryImage } from '../../types';
import './Gallery.css';

interface ApiGalleryImage {
	id: string;
	url: string;
	caption: string;
	order: number;
}

/**
 * Gallery carousel component
 * With modern navigation and animations
 * Now supports dynamic images from admin settings
 */
const Gallery: React.FC = () => {
	const { t } = useTranslation();
	const { ref: sectionRef, isVisible } = useScrollReveal({ threshold: 0.1 });
	const [apiImages, setApiImages] = useState<ApiGalleryImage[]>([]);

	// 从 API 获取图片
	useEffect(() => {
		const fetchGalleryImages = async () => {
			try {
				const response = await fetch('/api/settings');
				const result = await response.json();
				if (result.success && result.settings?.galleryImages?.length > 0) {
					setApiImages(result.settings.galleryImages.sort((a: ApiGalleryImage, b: ApiGalleryImage) => a.order - b.order));
				}
			} catch (error) {
				console.error('Failed to fetch gallery images:', error);
			}
		};
		fetchGalleryImages();
	}, []);

	// 默认图片
	const defaultItems: GalleryImage[] = useMemo(
		() => [
			{ src: chefCooking, alt: t('gallery.items.chef', 'Professional Hibachi Chef'), description: t('gallery.items.chefDesc', 'Professional Hibachi Chef in Action') },
			{ src: gallery1, alt: t('gallery.items.grill', 'Hibachi Grill Setup'), description: t('gallery.items.grillDesc', 'Premium Hibachi Grill Setup') },
			{ src: food1, alt: t('gallery.items.steak', 'Hibachi Steak'), description: t('gallery.items.steakDesc', 'Delicious Hibachi Steak') },
			{ src: gallery2, alt: t('gallery.items.vegetables', 'Hibachi Vegetables'), description: t('gallery.items.vegetablesDesc', 'Fresh Hibachi Vegetables') },
			{ src: food2, alt: t('gallery.items.shrimp', 'Hibachi Shrimp'), description: t('gallery.items.shrimpDesc', 'Amazing Hibachi Shrimp') },
			{ src: gallery3, alt: t('gallery.items.food', 'Food Presentation'), description: t('gallery.items.foodDesc', 'Beautiful Food Presentation') },
		],
		[t]
	);

	// 如果有 API 图片则使用，否则使用默认图片
	const galleryItems: GalleryImage[] = useMemo(() => {
		if (apiImages.length > 0) {
			return apiImages.map((img, idx) => ({
				src: img.url,
				alt: img.caption || `Gallery Image ${idx + 1}`,
				description: img.caption || `Gallery Image ${idx + 1}`,
			}));
		}
		return defaultItems;
	}, [apiImages, defaultItems]);

	const { currentSlide, nextSlide, prevSlide, goToSlide } = useSlider({
		totalSlides: galleryItems.length,
		autoPlay: true,
		intervalMs: 4000,
	});

	const currentImage = galleryItems[currentSlide];

	return (
		<section className="gallery" id="gallery" ref={sectionRef as React.RefObject<HTMLElement>}>
			<div className={`gallery-content ${isVisible ? 'visible' : ''}`}>
				<h2>{t('gallery.title', 'GALLERY')}</h2>
				<p className="gallery-subtitle">
					{t('gallery.subtitle', 'Experience the Family Friends Hibachi difference')}
				</p>

				<div className="gallery-container">
					<div className="gallery-slide">
						<img 
							src={currentImage.src} 
							alt={currentImage.alt} 
							loading="lazy" 
							key={currentSlide}
						/>
						<div className="gallery-caption">
							<h3>{currentImage.description}</h3>
							<p>{currentSlide + 1} / {galleryItems.length}</p>
						</div>
					</div>

					<button
						className="gallery-btn prev"
						onClick={prevSlide}
						aria-label={t('common.previous', 'Previous')}
						type="button"
					>
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path
								d="M15 18L9 12L15 6"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
					<button
						className="gallery-btn next"
						onClick={nextSlide}
						aria-label={t('common.next', 'Next')}
						type="button"
					>
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path
								d="M9 18L15 12L9 6"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>

					<div className="gallery-dots" role="tablist" aria-label="Gallery slides">
						{galleryItems.map((_, index) => (
							<button
								key={index}
								className={`dot ${index === currentSlide ? 'active' : ''}`}
								onClick={() => goToSlide(index)}
								aria-label={`${t('common.goTo', 'Go to')} ${index + 1}`}
								role="tab"
								aria-selected={index === currentSlide}
								type="button"
							/>
						))}
					</div>
				</div>

				{/* Thumbnails */}
				<div className="gallery-thumbnails">
					{galleryItems.map((item, index) => (
						<button
							key={index}
							className={`thumbnail ${index === currentSlide ? 'active' : ''}`}
							onClick={() => goToSlide(index)}
							type="button"
							aria-label={item.alt}
						>
							<img src={item.src} alt={item.alt} loading="lazy" />
						</button>
					))}
				</div>
			</div>
		</section>
	);
};

export default Gallery;
